import { getCredentials } from "@connect/credentials-storage";
import { getBaggage } from "@connect/observability";

const OPERATION_NAME = "isCardBINAllowed";

const QUERY = `query ${OPERATION_NAME}($cardBIN: String!) {
  isCardBINAllowed(cardBIN: $cardBIN) {
    id
    success
    cardPaymentType
    cardIssuingCountry
  }
}`;

export interface CardBinAllowedResult {
  /** false when the issuing BIN is on the disallow-list for this transaction. */
  success: boolean;
  /** "credit" | "debit" | null — the issuing card type reported by the BIN lookup. */
  cardPaymentType: "credit" | "debit" | null;
  /** ISO 3166-1 alpha-3 country of the issuing bank, or null when the lookup can't determine it. */
  cardIssuingCountry: string | null;
}

export interface FetchCardBinAllowedArgs {
  /** API origin (e.g. https://api.moonpay.com); "" for same-origin/local. */
  baseUrl: string;
  /** First 6-8 digits of the card number. */
  cardBin: string;
  /** Ground-control proxy target header value, when the frame carries one. */
  apiTarget?: string;
  signal?: AbortSignal;
}

// The BIN allow-list check only exists as a GraphQL query on the API, but Connect's
// data layer is otherwise REST/openapi-fetch. This is a deliberate one-off pure fetch
// that mirrors the auth/session headers the openapi-fetch middleware sets. Tracked for
// replacement by a REST wrapper endpoint (DEVXP follow-up to DEVXP-1055).
export async function fetchCardBinAllowed({
  baseUrl,
  cardBin,
  apiTarget,
  signal,
}: FetchCardBinAllowedArgs): Promise<CardBinAllowedResult> {
  const { customerToken, csrfToken } = await getCredentials();

  const headers: Record<string, string> = {
    "content-type": "application/json",
    baggage: getBaggage(),
  };
  if (customerToken) {
    headers.Authorization = `Bearer ${customerToken}`;
    headers["x-client-token"] = customerToken;
  }
  if (csrfToken) {
    headers["x-csrf-token"] = csrfToken;
  }
  if (apiTarget) {
    headers["x-api-target"] = apiTarget;
  }

  const response = await fetch(`${baseUrl}/graphql`, {
    method: "POST",
    credentials: "include",
    signal,
    headers,
    body: JSON.stringify({
      operationName: OPERATION_NAME,
      query: QUERY,
      variables: { cardBIN: cardBin },
    }),
  });

  if (!response.ok) {
    throw new Error(`BIN allow-list check failed (${response.status})`);
  }

  const json = (await response.json()) as {
    data?: {
      isCardBINAllowed?: {
        success: boolean;
        cardPaymentType: string | null;
        cardIssuingCountry: string | null;
      };
    };
    errors?: unknown[];
  };

  const result = json.data?.isCardBINAllowed;
  if (json.errors?.length || !result) {
    throw new Error("BIN allow-list check returned GraphQL errors");
  }

  return {
    success: result.success,
    // The API returns capitalized "Credit"/"Debit"; lowercase to match the union.
    cardPaymentType: (result.cardPaymentType?.toLowerCase() ?? null) as "credit" | "debit" | null,
    cardIssuingCountry: result.cardIssuingCountry ?? null,
  };
}
